import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { UserContext } from 'context';
import { validate } from 'services/ApiService';
import { getCookie, setCookie, deleteCookie } from 'helpers';

import Navbar from 'components/Navbars/Navbar';
import Footer from 'components/Footer/Footer.js';
import Sidebar from 'components/Sidebar/Sidebar';
import Loader from 'views/Loader';

import { navigableRoutes, internalRoutes, Route as AppRoute } from 'routes';

function flattenRoutes(routes: AppRoute[]): AppRoute[] {
  return routes.flatMap(route => route.children ? [route, ...flattenRoutes(route.children)] : [route]);
}

function AdminLayout() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('AdminLayout must be used within a UserContext.Provider');
  }

  const { setUser } = context;
  const history = useHistory();
  const mainPanel = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [wrapperClasses, setWrapperClasses] = useState('wrapper');

  const allRoutes = useMemo(() => {
    return [...flattenRoutes(navigableRoutes), ...internalRoutes];
  }, []);

  useEffect(() => {
    const tokenCookie = getCookie('accessToken');
    const tokenURL = new URLSearchParams(window.location.search).get('token');

    if (!tokenCookie && !tokenURL) {
      deleteCookie('accessToken');
      history.replace('/login');
      setLoading(false);
      return;
    }

    const token = tokenURL || tokenCookie;

    validate(token)
      .then(res => {
        if (!res.user) {
          deleteCookie('accessToken');
          history.replace('/login');
          console.debug('Autenticación fallida', res);
          return;
        }

        setUser(res.user);
        setCookie('accessToken', res.accessToken, { days: 10 });
        console.debug('Autenticación exitosa', res.user);
      })
      .catch(() => {
        deleteCookie('accessToken');
        history.replace('/login');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    setWrapperClasses(isSidebarOpen ? 'wrapper' : 'wrapper nav-open');
  };

  return (
    <div className={wrapperClasses}>
      <Sidebar />
      <div className="main-panel" ref={mainPanel}>
        <Navbar toggleSidebar={ toggleSidebar } />
        <div className="content">
          <Switch>
            {allRoutes.map(({ path, component }) => (
              <Route key={path} path={path} exact component={component} />
            ))}
          </Switch>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default AdminLayout;
